import type { ActivityMessage, ToolApproval } from "@/api/models"

/**
 * Find a tool approval that matches an activity message by call_id.
 * Returns the matching approval or null if no match found.
 */
export function findToolApprovalForActivity(
  activity: ActivityMessage,
  toolApprovals: ToolApproval[] | undefined
): ToolApproval | null {
  if (!toolApprovals || toolApprovals.length === 0) {
    return null
  }

  const callId = activity.details?.call_id
  if (typeof callId !== "string") {
    return null
  }

  return toolApprovals.find((approval) => approval.call_id === callId) ?? null
}

/**
 * Check if an activity message is a tool-related activity that could have an approval.
 */
export function isToolActivity(activity: ActivityMessage): boolean {
  const toolActivityTypes = [
    "tools",
    "web_search_started",
    "web_scrape_started",
    "library_search_started",
    "file_search_started",
    "microsoft_365_search_started",
    "tool_started",
  ]

  return (
    toolActivityTypes.includes(activity.activity_type) ||
    (activity.activity_type === "tools" && typeof activity.details?.call_id === "string")
  )
}

/**
 * Create a map of call_id to tool approval for efficient lookups.
 */
export function createToolApprovalMap(
  toolApprovals: ToolApproval[] | undefined
): Map<string, ToolApproval> {
  const map = new Map<string, ToolApproval>()

  if (!toolApprovals) {
    return map
  }

  for (const approval of toolApprovals) {
    map.set(approval.call_id, approval)
  }

  return map
}
